import { useNavigate } from 'react-router-dom';
import { InboxNotificationItem } from './inboxTypes';
import { formatInboxTimestamp, getNotificationTypeLabel, getNotificationTypeClass, inboxText } from './inboxUtils';
import { buildReportPath, type ReportSource } from '../../utils/reportUrlUtils';
import { Btn } from '../ui';

interface InboxDetailPanelProps {
    notification: InboxNotificationItem;
}

export const InboxDetailPanel = ({ notification }: InboxDetailPanelProps) => {
    const navigate = useNavigate();
    const typeLabel = getNotificationTypeLabel(notification.type);
    const typeClass = getNotificationTypeClass(notification.type);

    const formattedDate = formatInboxTimestamp(notification.timestamp);
    const reportId = inboxText(notification.reportId);

    const reportSource: ReportSource | null = notification.noticeOrigin === 'Daily Service'
        ? 'DailyServiceReport'
        : notification.noticeOrigin === 'Inspection'
          ? 'InspectionReport'
          : null;
    const reportPath = reportId && reportSource
        ? buildReportPath({
              id: reportId,
              source: reportSource,
              slugLabel: inboxText(notification.equipmentName) || 'report',
          })
        : null;

    return (
        <div className="inbox-detail">
            {/* Detail header */}
            <div className="inbox-detail-header">
                <div className="inbox-detail-header-top">
                    <span className={`inbox-type-badge ${typeClass}`}>{typeLabel}</span>
                    <span className="inbox-detail-date">{formattedDate}</span>
                </div>
                <h2 className="inbox-detail-title">{inboxText(notification.title)}</h2>
                <div className="inbox-detail-actions">
                    {inboxText(notification.issueId) && (
                        <Btn
                            variant="primary"
                            buttonText="View Issue"
                            buttonOnClick={() => navigate(`/issue/${inboxText(notification.issueId)}`)}
                        />
                    )}
                    {inboxText(notification.equipmentId) && (
                        <Btn
                            variant="secondary-outlined"
                            buttonText="View Equipment"
                            buttonOnClick={() =>
                                navigate(`/equipment/${inboxText(notification.unitId) || inboxText(notification.equipmentId)}`)
                            }
                        />
                    )}
                    {reportPath && (
                        <Btn
                            variant="secondary-outlined"
                            buttonText="View Report"
                            buttonOnClick={() => navigate(reportPath)}
                        />
                    )}
                </div>
            </div>

            {/* Description */}
            <div className="inbox-detail-section">
                <h3>Description</h3>
                <p>{inboxText(notification.description)}</p>
            </div>

            {/* Reported by */}
            {inboxText(notification.reportedBy) && (
                <div className="inbox-detail-section">
                    <h3>Reported By</h3>
                    <p className="inbox-detail-reporter">{inboxText(notification.reportedBy)}</p>
                    {inboxText(notification.reportedByContact) && (
                        <p className="inbox-detail-contact">{inboxText(notification.reportedByContact)}</p>
                    )}
                </div>
            )}

            {/* Related info */}
            <div className="inbox-detail-section">
                <h3>Related</h3>
                <div className="inbox-detail-related">
                    {inboxText(notification.equipmentName) && (
                        <div className="inbox-detail-related-row">
                            <span className="inbox-detail-related-label">Equipment</span>
                            <span
                                className="inbox-detail-related-value link"
                                onClick={() => navigate(`/equipment/${inboxText(notification.unitId) || inboxText(notification.equipmentId)}`)}
                            >
                                {inboxText(notification.equipmentName)}
                            </span>
                        </div>
                    )}
                    {inboxText(notification.locationName) && (
                        <div className="inbox-detail-related-row">
                            <span className="inbox-detail-related-label">Location</span>
                            <span className="inbox-detail-related-value">{inboxText(notification.locationName)}</span>
                        </div>
                    )}
                    {inboxText(notification.noticeOrigin) && (
                        <div className="inbox-detail-related-row">
                            <span className="inbox-detail-related-label">From</span>
                            <span className="inbox-detail-related-value">{inboxText(notification.noticeOrigin)}</span>
                        </div>
                    )}
                    {formattedDate && (
                        <div className="inbox-detail-related-row">
                            <span className="inbox-detail-related-label">Noticed</span>
                            <span className="inbox-detail-related-value">{formattedDate}</span>
                        </div>
                    )}
                    {reportPath && (
                        <div className="inbox-detail-related-row">
                            <span className="inbox-detail-related-label">
                                {notification.noticeOrigin === 'Daily Service' ? 'Daily Service' : 'Inspection'}
                            </span>
                            <span
                                className="inbox-detail-related-value link"
                                onClick={() => navigate(reportPath)}
                            >
                                {inboxText(notification.unitName) || inboxText(notification.equipmentName) || 'Report'}
                            </span>
                        </div>
                    )}
                    {inboxText(notification.issueTicket) && (
                        <div className="inbox-detail-related-row">
                            <span className="inbox-detail-related-label">Issue Ticket</span>
                            <span className="inbox-detail-related-value">{inboxText(notification.issueTicket)}</span>
                        </div>
                    )}
                    {inboxText(notification.equipmentStatus) && (
                        <div className="inbox-detail-related-row">
                            <span className="inbox-detail-related-label">Equipment Status</span>
                            <span className={`inbox-detail-status ${inboxText(notification.equipmentStatus) === 'Out of Service' ? 'status-critical' : 'status-warning'}`}>
                                ● {inboxText(notification.equipmentStatus)}
                            </span>
                        </div>
                    )}
                    {inboxText(notification.pmServiceLevel) && (
                        <div className="inbox-detail-related-row">
                            <span className="inbox-detail-related-label">Service Level</span>
                            <span className="inbox-detail-related-value">{inboxText(notification.pmServiceLevel)}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
